import { cartGroupIdentity, groupCartItems, changeCartGroupQuantity } from 'cart-line-groups';

/**
 * Nexbie customisations for the Prestige theme.
 *
 * Everything in this file is ours. It only hooks the events the theme already
 * dispatches, so it survives a Prestige theme update or a `shopify theme pull`,
 * which would overwrite theme.js.
 *
 * The product Add to cart button rides the native ProductForm lifecycle, with
 * two departures: the global top loading bar is silenced for that flow (the
 * button already has a spinner), and the drawer is filled from the cart/add.js
 * response before it opens instead of after theme.js's extra /cart.js round trip.
 *
 * [nexbie lazy-cart-drawer]
 * 抽屉「You May Also Like」推荐区已拆分为独立的静态 section (cart-drawer-recommendations)，
 * 由本文件在抽屉呈现时按需懒加载并插入外壳，避免拖慢加购响应与首屏 DOM。
 * 配合 theme.js 中的 replaceContent_fn 跳过与预填相同 HTML 的第二次重复重建。
 */
(() => {
 window.addEventListener('pageshow', () => {
  document.querySelectorAll('[data-payment-options-trigger][aria-busy="true"], [data-pdp-buynow][aria-busy="true"], .pdp-buy__atc[aria-busy="true"], .pdp-sticky-atc__btn[aria-busy="true"]').forEach((button) => {
   button.removeAttribute('aria-busy');
   button.disabled = false;
  });
  document.querySelectorAll('[data-pdp-buynow], .pdp-buy__atc, .pdp-sticky-atc__btn').forEach((btn) => {
   if (btn.getAttribute('data-pre-disabled') !== 'true') {
    btn.disabled = false;
   }
  });
 });
 const getCartDrawer = () => {
  const settings = window.themeVariables?.settings;

  if (!settings || settings.cartType !== 'drawer' || settings.pageType === 'cart') {
   return null;
  }

  return document.querySelector('cart-drawer');
 };

 const markDrawerBusyUntilContentSwap = (drawer) => {
  drawer.setAttribute('aria-busy', 'true');

  const clear = () => {
   observer.disconnect();
   clearTimeout(timer);
   drawer.removeAttribute('aria-busy');
  };
  const observer = new MutationObserver(clear);
  const timer = setTimeout(clear, 5000);

  observer.observe(drawer, { childList: true });
 };

 // When the cart content fits without overflowing (e.g. mobile, few items), the
 // drawer body has nothing to scroll — disable the touch drag so the gesture
 // doesn't bounce the page behind (drawer body gets touch-action: none via
 // [data-drag-disabled] in custom.css).
 const syncCartDrawerDrag = (drawer) => {
  const bodyPart = drawer?.getShadowPartByName?.('body');

  if (!bodyPart) {
   return;
  }

  drawer.toggleAttribute('data-drag-disabled', bodyPart.scrollHeight <= bodyPart.clientHeight + 1);
 };

 const cartDrawer = getCartDrawer();

 if (cartDrawer) {
  // dialog:after-show 不冒泡（theme.js dispatchEvent 未传 bubbles），必须直接监听元素本身
  cartDrawer.addEventListener('dialog:after-show', () => syncCartDrawerDrag(cartDrawer));
  cartDrawer.addEventListener('dialog:before-hide', () => cartDrawer.removeAttribute('data-drag-disabled'));

  // touch-action 只挡触摸；macOS 双指滑动走 wheel，这里在无需滚动时也拦截
  cartDrawer.addEventListener(
   'wheel',
   (event) => {
    if (cartDrawer.hasAttribute('data-drag-disabled')) {
     event.preventDefault();
    }
   },
   { passive: false }
  );
 }

 document.addEventListener('cart-drawer:refreshed', () => {
  syncCartDrawerDrag(getCartDrawer());
 });
 const PRODUCT_FORM_SELECTOR = 'form[action*="/cart/add"]';
 let pendingNativeForm = null;
 let nativeFormResetTimer;
 // 加购期间监听按钮 aria-busy 的 MutationObserver 句柄；clearPendingNativeForm / watchFormBusy 共用，
 // 缺少这行声明时 clearPendingNativeForm 会抛 ReferenceError，pendingNativeForm 永远设不上，
 // 顶部进度条静音与抽屉预填全部失效。
 let formBusyObserver = null;

 // ==========================================================================
 // Cart Drawer Recommendations Lazy Loading Module
 // ==========================================================================
 let recsSequence = 0;
 let recsCache = null; // { key: string, html: string }
 let drawerInAnimation = false;
 let pendingInsertFn = null;

 const getCartItemsKey = (drawer) => {
  if (!drawer) return '';
  const ids = Array.from(drawer.querySelectorAll('line-item[data-product-id]'))
   .map((el) => el.getAttribute('data-product-id'))
   .filter(Boolean);
  return ids.join(',');
 };

 const setupDrawerAnimationTracking = (drawer) => {
  if (!drawer || drawer._recsAnimTracked) return;
  drawer._recsAnimTracked = true;

  drawer.addEventListener('dialog:before-show', () => {
   drawerInAnimation = true;
  });

  drawer.addEventListener('dialog:after-show', () => {
   drawerInAnimation = false;
   if (pendingInsertFn) {
    const fn = pendingInsertFn;
    pendingInsertFn = null;
    fn();
   }
  });

  drawer.addEventListener('dialog:after-hide', () => {
   drawerInAnimation = false;
   pendingInsertFn = null;
  });
 };

 const renderRecommendationsIntoDrawer = (drawer, html, key) => {
  const shell = drawer.querySelector('.cart-drawer__complementary-products');
  const carousel = shell?.querySelector('scroll-carousel');
  if (!shell || !carousel) return;

  const temp = document.createRange().createContextualFragment(html);
  const container = temp.querySelector('.cart-drawer-recommendations');
  const cards = Array.from((container || temp).querySelectorAll('.horizontal-product-card'));

  // 过滤已在同一次页面浏览中被用户删除的商品
  const filteredCards = cards.filter((card) => {
   const pid = card.getAttribute('data-product-id') || card.dataset.productId || card.querySelector('[data-product-id]')?.dataset.productId;
   return !pid || !removedCartProductIds.has(String(pid));
  });

  // 0 张时不清空轮播：scroll-carousel 在子节点变动后会读 cells[0] 的样式（theme.js cellAlign），
  // 清成空会抛 getComputedStyle 错。外壳下面整块 hidden，里面残留的骨架 / 旧卡片不可见，下次渲染整体替换。
  if (filteredCards.length > 0) {
   carousel.replaceChildren(...filteredCards);
  }
  shell.setAttribute('data-recs-key', key);

  const arrowGroup = shell.querySelector('.h-stack.gap-2');
  if (filteredCards.length === 0) {
   shell.setAttribute('hidden', '');
   arrowGroup?.setAttribute('hidden', '');
  } else {
   shell.removeAttribute('hidden');
   if (filteredCards.length > 1) {
    arrowGroup?.removeAttribute('hidden');
   } else {
    arrowGroup?.setAttribute('hidden', '');
   }
  }

  syncCartDrawerDrag(drawer);
 };

 // 推荐区走网络拉取期间的骨架卡（样式见 custom.css 的 .cart-recs-skeleton__*）。
 // 外壳里已有可见的真实卡片时不换：旧卡片一直显示到新结果替换，避免「真卡 → 骨架 → 真卡」闪一下。
 // 骨架由 renderRecommendationsIntoDrawer 的 replaceChildren 换掉；无推荐或拉取失败时整个外壳照旧 hidden，
 // 骨架随之消失，不会承诺不来的内容。
 const showRecommendationsSkeleton = (drawer) => {
  const shell = drawer.querySelector('.cart-drawer__complementary-products');
  const carousel = shell?.querySelector('scroll-carousel');
  if (!shell || !carousel) return;
  if (!shell.hidden && carousel.querySelector('.horizontal-product-card:not(.cart-recs-skeleton)')) return;

  const bar = (...mods) => {
   const el = document.createElement('span');
   el.className = ['cart-free-gift-skeleton', ...mods].join(' ');
   return el;
  };
  const card = document.createElement('div');
  card.className = 'horizontal-product-card cart-recs-skeleton';
  card.setAttribute('aria-hidden', 'true');
  const figure = document.createElement('span');
  figure.className = 'horizontal-product-card__figure';
  figure.append(bar('cart-recs-skeleton__media'));
  const info = document.createElement('div');
  info.className = 'horizontal-product-card__info';
  const text = document.createElement('div');
  text.className = 'cart-recs-skeleton__text';
  const title = document.createElement('div');
  title.className = 'cart-recs-skeleton__title';
  title.append(bar('cart-free-gift-skeleton--title'), bar('cart-free-gift-skeleton--title', 'cart-free-gift-skeleton--title-short'));
  text.append(title, bar('cart-free-gift-skeleton--price'));
  info.append(text, bar('cart-recs-skeleton__button'));
  card.append(figure, info);

  carousel.replaceChildren(card);
  shell.querySelector('.h-stack.gap-2')?.setAttribute('hidden', '');
  shell.removeAttribute('data-recs-key');
  shell.removeAttribute('hidden');
 };

 // 立即隐藏推荐区并作废尚未插入的推荐结果，供删除商品时调用；
 // 清掉 data-recs-key 以便下一次 loadCartRecommendations 重新渲染。
 const hideCartRecommendations = (drawer) => {
  recsSequence += 1;
  pendingInsertFn = null;
  const shell = drawer?.querySelector('.cart-drawer__complementary-products');
  shell?.setAttribute('hidden', '');
  shell?.removeAttribute('data-recs-key');
 };

 const loadCartRecommendations = async (drawer, { forceImmediate = false } = {}) => {
  if (!drawer) return;
  setupDrawerAnimationTracking(drawer);

  const key = getCartItemsKey(drawer);
  if (!key) {
   // 购物车为空
   const shell = drawer.querySelector('.cart-drawer__complementary-products');
   shell?.setAttribute('hidden', '');
   return;
  }

  const shell = drawer.querySelector('.cart-drawer__complementary-products');
  const currentRenderedKey = shell?.getAttribute('data-recs-key');
  if (currentRenderedKey === key && shell?.querySelector('.horizontal-product-card:not(.cart-recs-skeleton)')) {
   // 已经渲染且 key 一致，仅确保过滤与可见性
   return;
  }

  const seq = ++recsSequence;

  const scheduleInsert = (html) => {
   const insertAction = () => {
    if (seq !== recsSequence || !drawer.isConnected) return;
    renderRecommendationsIntoDrawer(drawer, html, key);
   };

   if (forceImmediate || !drawerInAnimation) {
    insertAction();
   } else {
    pendingInsertFn = insertAction;
   }
  };

  // 命中单条缓存
  if (recsCache && recsCache.key === key) {
   scheduleInsert(recsCache.html);
   return;
  }

  showRecommendationsSkeleton(drawer);

  try {
   const root = window.Shopify?.routes?.root || '/';
   const url = `${root}${root.endsWith('/') ? '' : '/'}?section_id=cart-drawer-recommendations`;
   const response = await fetch(url);
   if (!response.ok) throw new Error(`HTTP ${response.status}`);
   const html = await response.text();

   if (seq !== recsSequence || !drawer.isConnected) return;
   recsCache = { key, html };
   scheduleInsert(html);
  } catch (error) {
   console.warn('Failed to load cart recommendations:', error);
   if (seq === recsSequence && drawer.isConnected) {
    shell?.setAttribute('hidden', '');
   }
  }
 };
 const setFormBusy = (form, busy) => {
  const formButtons = Array.from(form.elements)
   .filter((element) => element instanceof HTMLButtonElement && element.type === 'submit');

  formButtons.forEach((button) => {
   if (busy) {
    button.setAttribute('aria-busy', 'true');
   } else {
    button.removeAttribute('aria-busy');
   }
  });

  // [nexbie us-parity] 参考美国鞋站：加购与 Buy Now 互斥置灰
  const section = form.closest('.pdp-custom, .shopify-section') || document;
  const buyNowButtons = Array.from(section.querySelectorAll('[data-pdp-buynow]'));
  const allAtcButtons = Array.from(document.querySelectorAll('.pdp-buy__atc, .pdp-sticky-atc__btn'));

  if (busy) {
   buyNowButtons.forEach((btn) => {
    btn.disabled = true;
    btn.setAttribute('disabled', '');
   });
  } else {
   buyNowButtons.forEach((btn) => {
    btn.disabled = false;
    btn.removeAttribute('disabled');
   });
   allAtcButtons.forEach((btn) => {
    btn.removeAttribute('aria-busy');
   });
  }
 };

 const clearPendingNativeForm = () => {
  clearTimeout(nativeFormResetTimer);
  formBusyObserver?.disconnect();
  formBusyObserver = null;
  if (pendingNativeForm) {
   setFormBusy(pendingNativeForm, false);
  }
  pendingNativeForm = null;
  // 确保所有 data-pdp-buynow 按钮恢复可用
  document.querySelectorAll('[data-pdp-buynow]').forEach((btn) => {
   btn.disabled = false;
   btn.removeAttribute('disabled');
  });
 };
 const watchFormBusy = (form) => {
  // theme.js's native ProductForm removes aria-busy as soon as cart/add.js
  // resolves, but the drawer refresh (cart.js + section render) still needs
  // time on a slow mobile connection. Re-add the pending state the moment it
  // drops, so the spinner never blinks off mid-flow.
  formBusyObserver?.disconnect();
  const buttons = Array.from(form.elements).filter((element) => element instanceof HTMLButtonElement && element.type === 'submit');
  formBusyObserver = new MutationObserver((mutations) => {
   if (!pendingNativeForm) {
    return;
   }
   mutations.forEach((mutation) => {
    if (mutation.attributeName === 'aria-busy' && mutation.target.getAttribute('aria-busy') !== 'true') {
     mutation.target.setAttribute('aria-busy', 'true');
    }
   });
  });
  buttons.forEach((button) => formBusyObserver.observe(button, { attributes: true, attributeFilter: ['aria-busy'] }));
 };

 /**
  * Silence the global top loading bar for the add-to-cart flow only.
  *
  * The bar (<loading-bar>, theme.liquid) is a single global element fed by seven
  * different flows in theme.js. For add to cart it is pure duplication: every
  * buy button already renders its own spinner via [aria-busy] (see the
  * .pdp-buy__atc / .pdp-sticky-atc__btn / .pdp-fbt__add-btn rules in
  * main-product-custom.liquid). The bar only adds a ~2s white sweep across the
  * top that competes with the drawer sliding in.
  *
  * theme.js dispatches these on documentElement with bubbles:true and LoadingBar
  * listens on document in the bubble phase, so a capture-phase listener on
  * document runs first. LoadingBar is the only listener of these two events in
  * the whole theme (verified by grep), so stopImmediatePropagation is safe.
  *
  * Scope is the flow, not the event source: pendingNativeForm is set only by the
  * ProductForm submit below and cleared on success, failure and timeout, so
  * collection filtering, predictive search, quick buy, the shipping estimator and
  * the MaxBundle flow all keep their bar.
  *
  * If theme.js ever renames these events this listener simply stops matching and
  * the bar comes back — a degradation to today's behaviour, not a breakage.
  */
 ['theme:loading:start', 'theme:loading:end'].forEach((eventName) => {
  document.addEventListener(
   eventName,
   (event) => {
    if (pendingNativeForm) {
     event.stopImmediatePropagation();
    }
   },
   true
  );
 });

 document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(PRODUCT_FORM_SELECTOR) || window.themeVariables?.settings?.cartType !== 'drawer' || !form.checkValidity()) {
   return;
  }
  clearPendingNativeForm();
  cartAddFailed = false;
  drawerRefreshSeen = false;
  barEndedForFlow = false;
  pendingNativeForm = form;

  // [nexbie us-parity] 点击加购提交时，立即禁用 Buy Now 按钮，防止慢网络下同时触发两个动作
  const section = form.closest('.pdp-custom, .shopify-section') || document;
  section.querySelectorAll('[data-pdp-buynow]').forEach((btn) => {
   btn.disabled = true;
  });
  // Do NOT set aria-busy here: theme.js's ProductForm aborts the submit if
  // the submitter is already busy (event.submitter aria-busy guard). It sets
  // busy itself, and watchFormBusy re-adds it whenever theme.js drops it
  // before the drawer refresh completes.
  watchFormBusy(form);
  nativeFormResetTimer = setTimeout(clearPendingNativeForm, 10000);
 }, { capture: true });

 document.addEventListener('click', (event) => {
  const atc = event.target instanceof Element ? event.target.closest('.pdp-buy__atc, .pdp-sticky-atc__btn, button[name="add"]') : null;
  if (!atc || atc.disabled || atc.getAttribute('aria-busy') === 'true') return;
  const section = atc.closest('.pdp-custom, .shopify-section') || document;
  section.querySelectorAll('[data-pdp-buynow]').forEach((btn) => {
   btn.disabled = true;
   btn.setAttribute('disabled', '');
  });
 }, { capture: true });

 document.addEventListener('cart:change', (event) => {
  if (event.detail?.baseEvent === 'variant:add' && pendingNativeForm) {
   // 如果 drawer 已经打开并预填完毕（即 pendingNativeForm 已经由 fillCartDrawer 清除），
   // 不再重新把按钮置为 busy
   // (section fetch) to finish before its contents update. If that never
   // completes, re-sync and force the update after a grace period.
   clearTimeout(drawerRecoveryTimer);
   drawerRecoveryTimer = setTimeout(() => {
    if (!drawerRefreshSeen) {
     recoverStuckDrawer();
    }
   }, 8000);
  }
 });


 /**
  * MaxBundle bundle add-to-cart.
  *
  * MaxBundle shows a one-item popup and waits 1.5 seconds before redirecting,
  * so we reach into its Vue instance to switch that off. It then only leaves
  * us the cart:refresh event to react to. The drawer is shown while its
  * contents are fetched, with the bundle button remaining busy until the
  * refreshed contents arrive.
  */
 const MAX_BUNDLE_BUTTON_SELECTOR = '.cxBundleBtn.mb_fbt_bundle_btn';

 let pendingBundleButton = null;
 let bundleResetTimer;

 const suppressMaxBundlePopupDelay = (button) => {
  const appRoot = button.closest('.bundle-offer-container');
  const component = appRoot?._vnode?.component;
  const app = component?.proxy;

  if (!app || !('bundleButtonEvent' in app)) {
   return false;
  }

  app.bundleButtonEvent = '1';

  if (component.data) {
   component.data.notification_popup_show = false;
  }

  return true;
 };

 const clearPendingBundleButton = () => {
  clearTimeout(bundleResetTimer);
  pendingBundleButton?.removeAttribute('aria-busy');
  pendingBundleButton = null;
 };
document.addEventListener('cart-drawer:refreshed', (event) => {
 drawerRefreshSeen = true;
 clearTimeout(drawerRecoveryTimer);
 clearPendingNativeForm();
 clearPendingBundleButton();
 document.querySelectorAll('[data-pdp-buynow]').forEach((btn) => {
  btn.disabled = false;
  btn.removeAttribute('disabled');
 });
});

 /**
  * Add-to-cart failure containment.
  *
  * Prestige's ProductForm (theme.js) runs the /cart/add.js fetch without any
  * try/catch/finally. When that request rejects (dropped connection, Cloudflare
  * challenge), returns a non-JSON error body (Shopify's local_rate_limited 429,
  * Fastly/Cloudflare 5xx HTML), or hangs, the async chain throws and the UI is
  * left stuck: the ATC spinner is only released by our 10s fallback timer, the
  * cart drawer never opens, and no error is shown. We watch the exact request
  * ProductForm posts and recover the UI.
  */
 const CART_ADD_URL_PATTERN = /\/cart\/add\.js(\?.*)?$/;
 // How long to wait before giving up on the UI (spinner, top bar) while the
 // request is still in flight. The request itself is never aborted for the
 // ProductForm flow — it may still complete server-side and open the drawer.
 const CART_ADD_TIMEOUT_MS = 10000;
 const DEFAULT_CART_ADD_ERROR = "Couldn't add the item to your cart. Please try again.";
 const nativeFetch = window.fetch;
 let cartAddFailed = false;
 let drawerRefreshSeen = false;
 let drawerRecoveryTimer;
 let barEndedForFlow = false;

 /**
  * Close out the loading bar for a flow that died before theme.js could.
  *
  * For the ProductForm flow the bar is silenced upstream (see the capture-phase
  * listener above), so this is a no-op there and only matters if that silencing
  * ever stops matching. It is kept because the recovery paths below can also run
  * for a flow whose bar was never silenced, and leaving the bar parked at
  * scaleX(0.4) is worse than an extra end event that LoadingBar ignores.
  */
 const endLoadingBar = () => {
  if (barEndedForFlow) {
   return;
  }
  barEndedForFlow = true;
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
 };

 const showCartAddError = (host, message) => {
  host.querySelector('.banner--error')?.remove();

  const banner = document.createElement('div');
  banner.className = 'banner banner--error';
  banner.setAttribute('role', 'alert');
  banner.textContent = message;
  host.prepend(banner);
  setTimeout(() => banner.remove(), 4500);
 };

 const failCartAdd = (reason, form) => {
  if (!form || cartAddFailed) {
   return;
  }

  cartAddFailed = true;
  console.error(`Add to cart failed: ${reason}`);
  clearPendingNativeForm();
  endLoadingBar();
  showCartAddError(form, `${DEFAULT_CART_ADD_ERROR} (${reason})`);
 };

 document.addEventListener('cart:error', (event) => {
  const form = pendingNativeForm;
  clearPendingNativeForm();

  // The custom PDP ships no <buy-buttons> element, so the theme's error banner
  // has nowhere to render for JSON error responses (out of stock, quantity
  // limit, ...). Surface those failures ourselves when nothing else will.
  if (form && !document.querySelector('buy-buttons')) {
   showCartAddError(form, event.detail?.error || DEFAULT_CART_ADD_ERROR);
  }
 });

 /**
  * Fill the drawer from the section markup cart/add.js already returned.
  *
  * ProductForm posts `sections=cart-drawer` with the add (theme.js), so the
  * rendered drawer is in the add response itself. theme.js still waits for a
  * second, serial GET /cart.js before it dispatches cart:change and swaps the
  * content in, which is why the drawer used to slide out showing the *previous*
  * cart — "Your cart is empty" for 352ms in the measured baseline.
  *
  * Filling here lets us open the drawer with the correct content straight away.
  * theme.js's own replaceContent still runs ~350ms later with the same markup,
  * which is a no-op visually; the second round trip is demoted from "blocks the
  * drawer" to "background reconciliation" that keeps <cart-count>, the free
  * shipping bar and fetchCart in sync.
  *
  * Mirrors theme.js's own unwrapping (section wrapper -> <cart-drawer> ->
  * innerHTML). Returns false on any surprise so the caller can fall back to the
  * old open-then-fill behaviour instead of throwing.
  */
 const fillCartDrawer = (drawer, sections) => {
  if (!sections || typeof sections !== 'object') {
   return false;
  }

  try {
   // Look the drawer up by element, never by key or section id. Two reasons:
   // the key is the *section id*, which is group-prefixed when the drawer lives
   // in a section group (currently "sections--<id>__cart-drawer", and it changes
   // whenever the group is recreated); and Prestige moves <cart-drawer> out to
   // <body> once it upgrades, so the live element is no longer inside its
   // .shopify-section wrapper and cannot be matched against that id anyway.
   let matchedHtml = null;
   const source = Object.values(sections)
    .filter((html) => typeof html === 'string' && html !== '')
    .map((html) => {
     const el = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
     if (el && !matchedHtml) {
      matchedHtml = html;
     }
     return el;
    })
    .find(Boolean);

   if (!source) {
    return false;
   }

   const fragment = document.createRange().createContextualFragment(source.innerHTML);

   if (fragment.children.length === 0) {
    return false;
   }

   drawer.replaceChildren(...fragment.children);
   drawer.classList.remove('drawer--center-body');

   // [nexbie lazy-cart-drawer] 记录预填使用的原始 section HTML，供 theme.js replaceContent_fn 比对跳过重复重建
   if (matchedHtml) {
    drawer._prefilledSectionHtml = matchedHtml;
   }
   resetDrawerState();
   primeDrawerState(drawer);

   // [nexbie us-parity] 抽屉预填成功，恢复 Buy Now 按钮可用状态
   document.querySelectorAll('[data-pdp-buynow]').forEach((btn) => {
    btn.disabled = false;
    btn.removeAttribute('disabled');
   });

   return true;
  } catch (error) {
   console.warn('Cart drawer pre-fill skipped:', error);

   return false;
  }
 };

 window.fetch = (input, init = {}) => {
  const method = (init.method || (typeof input === 'string' || input instanceof URL ? 'GET' : 'POST')).toUpperCase();
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : '';

  if (method !== 'POST' || !CART_ADD_URL_PATTERN.test(url)) {
   return nativeFetch(input, init);
  }

  // Only ProductForm submits set pendingNativeForm before fetching.
  const formForFlow = pendingNativeForm;

  if (formForFlow) {
   // ProductForm flow: deliberately never abort the request. A slow storefront
   // can still complete the add, and aborting would show a "canceled" request
   // in DevTools plus a false failure banner. After the timeout we only
   // recover the UI (spinner, top bar) and let the request finish; if it
   // succeeds, theme.js opens the drawer itself.
   const request = nativeFetch(input, init);
   let slowBannerShown = false;

   const timeout = setTimeout(() => {
    slowBannerShown = true;
    clearPendingNativeForm();
    endLoadingBar();
    showCartAddError(formForFlow, 'Still adding to your cart — please wait.');
   }, CART_ADD_TIMEOUT_MS);

   request.then(
    async (response) => {
     clearTimeout(timeout);
     // theme.js consumes the body with response.json(), which parses any text
     // regardless of Content-Type (Shopify's cart/add.js 200 responses are
     // text/javascript, error bodies are text/plain or HTML). Mirror that:
     // if the body is not JSON, theme.js will throw and strand the UI.
     let addResponse;

     try {
      addResponse = await response.clone().json();
     } catch {
      failCartAdd(`HTTP ${response.status}`, formForFlow);
      return;
     }
     if (slowBannerShown) {
      formForFlow.querySelector('.banner--error')?.remove();
     }

     // Content first, then open. The add response already carries the rendered
     // drawer, so we never have to show the pre-add cart. Never open for error
     // responses (e.g. 422 out of stock) — the theme surfaces those via
     // cart:error.
     if (response.ok) {
      const drawer = getCartDrawer();

      if (drawer) {
       if (!fillCartDrawer(drawer, addResponse?.sections)) {
        // Fallback to the old open-then-fill: theme.js will swap the content in
        // once its /cart.js round trip lands. Dim the stale contents until then.
        markDrawerBusyUntilContentSwap(drawer);
       }

       if (!drawer.hasAttribute('open')) {
        drawer.show();
       }

       // [nexbie lazy-cart-drawer] 在 drawer.show() 后发起推荐懒加载，动画结束后插入
       loadCartRecommendations(drawer);

       // [nexbie us-parity] 对齐美国鞋站：加购成功响应返回并唤起抽屉后，立即解除按钮忙碌
       // 注意：不要在此处清空 pendingNativeForm（保持引用直到 theme:loading:end 与 cart-drawer:refreshed），
       // 否则 theme.js 紧接着派发的 theme:loading:end 将无法被静音，导致顶部进度条跳出
       setFormBusy(formForFlow, false);
      }
      clearTimeout(drawerRecoveryTimer);
      drawerRecoveryTimer = setTimeout(() => {
       if (!drawerRefreshSeen) {
        recoverStuckDrawer();
       }
      }, 8000);
     }
    },
    () => {
     clearTimeout(timeout);
     failCartAdd('network error', formForFlow);
    }
   );

   return request;
  }

  // Other cart/add.js callers (FBT bulk add, MaxBundle, video-modal cards):
  // abort requests that hang so their own error handling runs; no banner.
  const controller = new AbortController();
  const signal = init.signal
   ? (typeof AbortSignal.any === 'function' ? AbortSignal.any([init.signal, controller.signal]) : init.signal)
   : controller.signal;
  const request = nativeFetch(input, { ...init, signal });
  const timeout = setTimeout(() => controller.abort(), CART_ADD_TIMEOUT_MS);
  request.then(() => clearTimeout(timeout), () => clearTimeout(timeout));

  return request;
 };

 window.addEventListener(
  'click',
  (event) => {
   if (window.themeVariables?.settings?.pageType !== 'product') {
    return;
   }

   if (!(event.target instanceof Element)) {
    return;
   }

   const button = event.target.closest(MAX_BUNDLE_BUTTON_SELECTOR);

   if (!button || !suppressMaxBundlePopupDelay(button)) {
    return;
   }

   clearPendingBundleButton();
   pendingBundleButton = button;
   button.setAttribute('aria-busy', 'true');
   bundleResetTimer = setTimeout(clearPendingBundleButton, 10000);
  },
  true
 );

 const getSectionId = (element) => {
  const wrapper = element.closest('[id^="shopify-section-"]');

  return wrapper ? wrapper.id.slice('shopify-section-'.length) : null;
 };

 const syncCartFromServer = async (drawer) => {
  const sectionId = getSectionId(drawer);

  if (!sectionId) {
   return;
  }

  const root = window.Shopify.routes.root;
  const [cart, sectionHtml] = await Promise.all([
   fetch(`${root}cart.js`).then((response) => response.json()),
   fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`).then((response) => response.text())
  ]);

  cart.sections = { [sectionId]: sectionHtml };

  document.documentElement.dispatchEvent(
   new CustomEvent('cart:change', {
    bubbles: true,
    detail: { baseEvent: 'variant:add', cart }
   })
  );
 };

 const recoverStuckDrawer = async () => {
  const drawer = getCartDrawer();

  if (!drawer || drawerRefreshSeen) {
   return;
  }

  // The storefront fetches may still be hanging; give the sync a bounded
  // window so the drawer can still show the new item.
  await Promise.race([syncCartFromServer(drawer), new Promise((resolve) => setTimeout(resolve, 4000))]);
  await drawer.show();
 };
 const waitForDrawerRefresh = (drawer) => new Promise((resolve) => {
  const timer = setTimeout(resolve, 10000);
  drawer.addEventListener('cart-drawer:refreshed', () => {
   clearTimeout(timer);
   resolve();
  }, { once: true });
 });

 const finishMaxBundleAdd = async (drawer) => {
  const refreshed = waitForDrawerRefresh(drawer);
  await syncCartFromServer(drawer);
  await refreshed;
  clearPendingBundleButton();
  await drawer.show();
 };

 window.addEventListener(
  'cart:refresh',
  async () => {
   if (!pendingBundleButton) {
    return;
   }

   const drawer = getCartDrawer();

   if (!drawer) {
    window.location.assign(`${window.Shopify.routes.root}cart`);
    return;
   }

   markDrawerBusyUntilContentSwap(drawer);
   await finishMaxBundleAdd(drawer);
  },
  true
 );

 /* ------------------------------------------------------------------------
    Optimistic quantity changes inside the cart drawer

    Prestige's LineItemQuantity (theme.js) posts /cart/change.js and then lets
    CartDrawer replace the whole drawer, so a click does nothing at all until
    the round trip lands and then the entire list is rebuilt — scroll position
    and focus are lost. Worse, the target quantity is baked into each button's
    href at render time, so two fast clicks both post the same number and the
    second one is silently dropped.

    We take the flow over before theme.js sees it and mirror what nexbie_us
    does: update the DOM immediately, coalesce clicks, post once, then quietly
    reconcile against the server. Nothing here is dispatched as cart:change
    while the drawer is open, because that is what triggers the rebuild.

    Scope is decided by DOM ownership — the event has to originate inside
    <cart-drawer> — so the cart page (which reloads the whole page instead,
    theme.js:1655) and the quick order list are untouched without having to
    enumerate them.
    ------------------------------------------------------------------------ */

 const DRAWER_QTY_DEBOUNCE_MS = 300;
 const CHANGE_LINK_SELECTOR = 'a[href*="/cart/change"]';
 const DELETE_LINK_CLASS = 'cart-drawer-line-item__delete';
 // "My Cart(4)" in the drawer header slot — the only number in that heading.
 const COUNT_IN_PARENS = /\(\s*\d+\s*\)/;
 // Written by snippets/cart-free-gift-data.liquid on every gift line, mirrored
 // onto the row by snippets/cart-drawer-gift-line-item.liquid.
 const GIFT_LINE_PROPERTY = '_free_gift';

 // Shopify.formatMoney ships with option_selection.js, which this theme does
 // not load, so money has to be rendered here. Only the placeholders Shopify
 // actually supports are handled; anything unknown leaves the label alone.
 const formatMoney = (cents) => {
  const format = window.themeVariables?.settings?.moneyFormat;

  if (typeof format !== 'string' || format === '') {
   return null;
  }

  const group = (value, separator) => value.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${separator}`);
  const build = (decimals, thousands, decimalMark) => {
   const amount = (Math.round(cents) / 100).toFixed(decimals);
   const [whole, fraction] = amount.split('.');

   return group(whole, thousands) + (fraction ? decimalMark + fraction : '');
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
   switch (name) {
    case 'amount':
     return build(2, ',', '.');
    case 'amount_no_decimals':
     return build(0, ',', '.');
    case 'amount_with_comma_separator':
     return build(2, '.', ',');
    case 'amount_no_decimals_with_comma_separator':
     return build(0, '.', ',');
    case 'amount_with_apostrophe_separator':
     return build(2, "'", '.');
    case 'amount_no_decimals_with_space_separator':
     return build(0, ' ', '.');
    case 'amount_with_space_separator':
     return build(2, ' ', ',');
    default:
     return match;
   }
  });
 };

 const parseMoney = (text) => {
  if (typeof text !== 'string') {
   return null;
  }

  // Keep the last separator group as the decimal mark so both 1,234.56 and
  // 1.234,56 land on the same number of cents.
  const digits = text.replace(/[^\d.,]/g, '');

  if (digits === '') {
   return null;
  }

  const lastMark = Math.max(digits.lastIndexOf('.'), digits.lastIndexOf(','));
  const whole = lastMark === -1 ? digits : digits.slice(0, lastMark);
  const fraction = lastMark === -1 ? '' : digits.slice(lastMark + 1);
  const cents = Number(whole.replace(/[.,]/g, '')) * 100 + Number((fraction + '00').slice(0, 2));

  return Number.isFinite(cents) ? cents : null;
 };

 // lineKey -> { quantity, unitCents, compareUnitCents }
 const drawerLineState = new Map();
 const drawerQtyTimers = new Map();
 let drawerSubtotalCents = null;
 let latestServerCart = null;
 let drawerStateDirty = false;

 const resetDrawerState = () => {
  drawerLineState.clear();
  drawerQtyTimers.forEach((timer) => clearTimeout(timer));
  drawerQtyTimers.clear();
  drawerSubtotalCents = null;
 };

 // Seed every line up front. stateFor() derives the current quantity from the
 // input, which is fine for button clicks but wrong for a typed change: by the
 // time the change event fires the input already holds the NEW value, so a
 // lazily seeded line would believe nothing changed and skip the update.
 const primeDrawerState = (drawer) => {
  if (!drawer) {
   return;
  }

  drawer.querySelectorAll('input[data-line-key]').forEach((input) => {
   stateFor(drawer, input.getAttribute('data-line-key'));
  });

  // Free gift rows carry no quantity input (their quantity is owned by
  // assets/cart-free-gift.js), so they are seeded from the row attribute or the
  // optimistic item count would drop by the number of gifts on every edit.
  drawer.querySelectorAll('line-item[data-line-key]').forEach((row) => {
   stateFor(drawer, row.getAttribute('data-line-key'));
  });
 };

// theme.js rebuilt the drawer (a new add, or our own empty-cart refresh), so
// every cached price and quantity is stale.
// [nexbie lazy-cart-drawer] 当 contentReplaced 为 false 时（跳过重复重建），
// 不清空正在进行中的乐观定时器与状态，仅 prime 新行。
document.addEventListener('cart-drawer:refreshed', (event) => {
 const drawer = event.target instanceof Element ? event.target.closest('cart-drawer') : getCartDrawer();
 if (event.detail?.contentReplaced !== false) {
  resetDrawerState();
  // 抽屉被整块重建后，这份快照描述的已经是另一份 DOM / 另一个购物车状态，
  // 留着会让队列发送前的 Key 重解拿旧数据去改写新行。
  latestServerCart = null;
 }
 primeDrawerState(drawer);

 // [nexbie us-parity] 确保所有 Buy Now 按钮完全解除禁用
 document.querySelectorAll('[data-pdp-buynow]').forEach((btn) => {
  btn.disabled = false;
  btn.removeAttribute('disabled');
 });

 // [nexbie lazy-cart-drawer] 抽屉刷新后的推荐处理
 if (drawer) {
  if (drawer.hasAttribute('open')) {
   loadCartRecommendations(drawer);
  } else {
   const key = getCartItemsKey(drawer);
   if (recsCache && recsCache.key === key) {
    renderRecommendationsIntoDrawer(drawer, recsCache.html, key);
   }
  }
 }
});

 // assets/cart-free-gift.js预测落空：抽屉不会再被重建，补上 applyServerCart 跳过的那次推荐加载。
 document.addEventListener('cart:gift-preview-dropped', () => {
  const drawer = getCartDrawer();

  if (drawer?.hasAttribute('open')) {
   loadCartRecommendations(drawer, { forceImmediate: true });
  }
 });

 document.addEventListener('dialog:before-show', (event) => {
  if (event.target instanceof Element && event.target.tagName === 'CART-DRAWER') {
   primeDrawerState(event.target);
   loadCartRecommendations(event.target);
  }
 }, true);

 // A free gift row has no quantity input, so it is addressed by the data-line-key
 // attribute snippets/cart-drawer-gift-line-item.liquid puts on the row.
 const lineRowFor = (drawer, lineKey) =>
  [...drawer.querySelectorAll('line-item')].find(
   (row) =>
    row.getAttribute('data-line-key') === lineKey ||
    row.querySelector(`input[data-line-key="${CSS.escape(lineKey)}"]`)
  ) || null;

 const priceSpansFor = (row) => {
  const host = row.querySelector('.cart-drawer-line-item__price');

  if (!host) {
   return { current: null, compare: null };
  }

  const spans = [...host.querySelectorAll('span')];

  return {
   current: spans.find((span) => !span.classList.contains('line-through')) || null,
   compare: spans.find((span) => span.classList.contains('line-through')) || null
  };
 };

 const stateFor = (drawer, lineKey) => {
  let state = drawerLineState.get(lineKey);

  if (state) {
   return state;
  }

  const row = lineRowFor(drawer, lineKey);
  const input = row?.querySelector(`input[data-line-key="${CSS.escape(lineKey)}"]`);
  const quantity = Math.max(0, parseInt(input?.value ?? row?.getAttribute('data-line-quantity'), 10) || 0);
  const { current, compare } = priceSpansFor(row || document.createElement('div'));
  const lineCents = parseMoney(current?.textContent);
  const compareCents = parseMoney(compare?.textContent);

  state = {
   quantity,
   unitCents: quantity > 0 && lineCents != null ? lineCents / quantity : null,
   compareUnitCents: quantity > 0 && compareCents != null ? compareCents / quantity : null,
   // The gift engine measures its tiers on the paid lines only (a gift must
   // never pay for itself), so the optimistic subtotal published below has to
   // know which rows to leave out.
   identity: row?.hasAttribute('data-cart-group') ? rowGroupIdentity(row) : null,
   gift: row?.getAttribute('data-free-gift') === 'true'
  };
  drawerLineState.set(lineKey, state);

  return state;
 };

 const currentSubtotal = (drawer) => {
  if (drawerSubtotalCents != null) {
   return drawerSubtotalCents;
  }

  const bar = drawer.querySelector('free-shipping-bar');
  const fromBar = bar ? parseFloat(bar.getAttribute('total-price')) : NaN;

  if (Number.isFinite(fromBar)) {
   drawerSubtotalCents = fromBar;
   return drawerSubtotalCents;
  }

  drawerSubtotalCents = parseMoney(drawer.querySelector('.cart-drawer__button-price')?.textContent) ?? 0;

  return drawerSubtotalCents;
 };

 // What assets/cart-free-gift.js would see if the optimistic quantities were
 // already on the server: the same non-gift subtotal its tiers are measured on.
 const optimisticGiftSubtotal = () =>
  [...drawerLineState.values()].reduce(
   (sum, entry) => (entry.gift ? sum : sum + (entry.unitCents ?? 0) * entry.quantity),
   0
  );

 const paintTotals = (drawer, subtotalCents, itemCount) => {
  drawerSubtotalCents = subtotalCents;

  const label = formatMoney(subtotalCents);

  if (label != null) {
   drawer.querySelectorAll('.cart-drawer__button-price').forEach((el) => {
    el.textContent = label;
   });
  }

  // Public setter: writes the attribute, which re-runs the bar's own message
  // and progress calculation. Cheaper and safer than re-rendering the section.
  const bar = drawer.querySelector('free-shipping-bar');

  if (bar) {
   bar.totalPrice = subtotalCents;
  }

  if (typeof itemCount === 'number') {
   const count = String(itemCount);

   // <cart-count> (header.liquid: "My Cart (N)") only ever learns a new number
   // from cart:change / cart:refresh, and everything above deliberately
   // withholds those while the drawer is open so theme.js cannot rebuild the
   // drawer under the user. Repaint it here — together with the nav badge,
   // which is server-rendered and has no JS of its own — or the header stays
   // frozen at the count the page was served with.
   document.querySelectorAll('cart-count, .uv-nav__cart-count').forEach((el) => {
    el.textContent = count;

    // An upgraded <cart-count> also exposes an itemCount setter (theme.js
    // writes innerText through it). Assign it too so the element's own API
    // reports the value just painted, whichever one a later listener reads.
    if ('itemCount' in el) {
     el.itemCount = itemCount;
    }
   });

   // cart-drawer.liquid renders "My Cart(N)" into the header slot and the
   // section is not re-rendered while the drawer is open, so swap the
   // parenthesised number in place.
   const heading = drawer.querySelector('[slot="header"]');
   const headingText = heading?.textContent ?? '';
   const nextHeadingText = headingText.replace(COUNT_IN_PARENS, `(${count})`);

   if (heading && nextHeadingText !== headingText) {
    heading.textContent = nextHeadingText;
   }
  }
 };

 // A row rendered without a line discount has no strikethrough span at all, so
 // the span has to be created (or dropped) before the prices can be painted —
 // otherwise crossing a discount tier would leave the original price invisible.
 const syncPriceSpans = (row, wantCompare) => {
  const host = row.querySelector('.cart-drawer-line-item__price');

  if (!host) {
   return { current: null, compare: null };
  }

  const spans = priceSpansFor(row);
  let compare = spans.compare;

  if (wantCompare && !compare) {
   compare = document.createElement('span');
   compare.className = 'text-subdued line-through';
   host.append(compare);
  } else if (!wantCompare && compare) {
   compare.remove();
   compare = null;
  }

  spans.current?.classList.toggle('text-on-sale', Boolean(compare));

  return { current: spans.current, compare };
 };

 const paintLine = (drawer, lineKey, quantity) => {
  const row = lineRowFor(drawer, lineKey);

  if (!row) {
   return;
  }

  if (quantity <= 0) {
   row.remove();
   return;
  }

  const input = row.querySelector(`input[data-line-key="${CSS.escape(lineKey)}"]`);

  if (input && input.value !== String(quantity)) {
   input.value = String(quantity);
   // Lets <quantity-input> resize itself and QuantitySelector refresh its
   // buttons. Deliberately not a "change" event — that is what theme.js's
   // LineItemQuantity delegate listens for.
   input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  const state = drawerLineState.get(lineKey);
  // A line discount wins over variant.compare_at_price, exactly like the
  // strikethrough branch order in cart-drawer-line-item.liquid.
  const compareUnitCents = state?.discountUnitCents ?? state?.compareUnitCents ?? null;
  const { current, compare } = syncPriceSpans(row, compareUnitCents != null);

  if (current && state?.unitCents != null) {
   const label = formatMoney(state.unitCents * quantity);

   if (label != null) {
    current.textContent = label;
   }
  }

  if (compare && compareUnitCents != null) {
   const label = formatMoney(compareUnitCents * quantity);

   if (label != null) {
    compare.textContent = label;
   }
  }
 };

const rowLineKey = (row) =>
	row.querySelector('input[data-line-key]')?.getAttribute('data-line-key') ||
	row.getAttribute('data-line-key') ||
	null;

 const rowGroupIdentity = (row) => cartGroupIdentity({
  variant_id: row.getAttribute('data-variant-id'),
  properties: JSON.parse(row.getAttribute('data-cart-properties') || '{}'),
  selling_plan_allocation: { selling_plan: { id: row.getAttribute('data-cart-plan') || '' } }
 });

 // A line key is <variant_id>:<properties_and_discounts_hash>. Crossing an
 // automatic discount tier (Buy 3 Get 15% Off) makes Shopify rehash the second
 // half, so the very same line comes back under a new key. Every key the front
 // end holds therefore has to follow the server: otherwise the sweep below
 // mistakes the row for a deleted one and removes it, and the next
 // /cart/change.js call posts a key the cart no longer knows.
 const migrateLineKey = (row, oldKey, newKey) => {
  if (!oldKey || !newKey || oldKey === newKey) {
   return;
  }

  row.querySelectorAll('[data-line-key]').forEach((el) => el.setAttribute('data-line-key', newKey));

  if (row.hasAttribute('data-line-key')) {
   row.setAttribute('data-line-key', newKey);
  }

  // The +/- and delete links carry ?id=<key>; that href is what the no-JS and
  // theme.js fallbacks post, so it must not keep pointing at the dead key.
  row.querySelectorAll('a[href]').forEach((link) => {
   const href = link.getAttribute('href');

   if (/[?&]id=/.test(href)) {
    link.setAttribute('href', href.replace(/([?&]id=)[^&]*/, `$1${newKey}`));
   }
  });

  const state = drawerLineState.get(oldKey);

  if (state) {
   drawerLineState.delete(oldKey);
   drawerLineState.set(newKey, state);
  }

  const timer = drawerQtyTimers.get(oldKey);

  if (timer !== undefined) {
   drawerQtyTimers.delete(oldKey);
   drawerQtyTimers.set(newKey, timer);
  }

  if (drawerPendingTargets.has(oldKey)) {
   drawerPendingTargets.set(newKey, drawerPendingTargets.get(oldKey));
   drawerPendingTargets.delete(oldKey);
  }
 };

 // Keep exact keys first, then pair by full identity so discount re-keying
 // never mixes different properties, selling plans, or gift and paid lines.
 const reconcileLineKeys = (drawer, items) => {
  const rows = [...drawer.querySelectorAll('line-item')];
  const groups = groupCartItems(items);
  const serverKeys = new Set(groups.map((item) => item.key));
  const matched = new Set(rows.map(rowLineKey).filter((key) => key && serverKeys.has(key)));
  const spares = new Map();
  rows.filter((row) => rowLineKey(row) && !matched.has(rowLineKey(row)) && row.hasAttribute('data-cart-properties'))
   .forEach((row) => spares.set(rowGroupIdentity(row), row));
  groups.forEach((item) => {
   if (matched.has(item.key)) return;
   const identity = item.groupIdentity || cartGroupIdentity(item);
   const row = spares.get(identity);
   if (!row) return;
   migrateLineKey(row, rowLineKey(row), item.key);
   spares.delete(identity);
   matched.add(item.key);
  });
 };

 const applyServerCart = (drawer, cart) => {
  latestServerCart = cart;

  reconcileLineKeys(drawer, cart.items);

  const present = new Set();

  groupCartItems(cart.items).forEach((item) => {
   present.add(item.key);

   const state = stateFor(drawer, item.key);
   state.quantity = item.quantity;

   // A gift line the drawer has not rendered yet (the gift write landed before
   // its section did) has no row to read the marker off, so trust the cart.
   if (item.properties?.[GIFT_LINE_PROPERTY] != null) {
    state.gift = true;
   }

   if (item.quantity > 0) {
    const row = lineRowFor(drawer, item.key);
    const variantCompare = Number(row?.getAttribute('data-variant-compare-price')) || 0;
    const variantPrice = Number(row?.getAttribute('data-variant-price')) || 0;
    const compareTotal = (item.lines || [item]).reduce((sum, member) => sum + (
     member.original_line_price > member.final_line_price || variantCompare <= variantPrice
      ? member.original_line_price : variantCompare * member.quantity
    ), 0);
    state.identity = item.groupIdentity || null;
    state.unitCents = item.final_line_price / item.quantity;
    state.discountUnitCents = null;
    state.compareUnitCents = compareTotal > item.final_line_price ? compareTotal / item.quantity : null;
   }

   paintLine(drawer, item.key, item.quantity);
  });

  [...drawerLineState.keys()].forEach((key) => {
   if (!present.has(key)) {
    drawerLineState.delete(key);
    paintLine(drawer, key, 0);
   }
  });

  drawer.querySelectorAll('line-item').forEach((row) => {
   const key = rowLineKey(row);

   if (key && !present.has(key)) {
    row.remove();
   }
  });

  paintTotals(drawer, cart.total_price, cart.item_count);

  // Last item gone: the drawer is already painted empty (paintEmpty, on the click), so
  // settle it against the real empty section without the theme's fade-out rebuild.
  if (cart.item_count === 0) {
   paintEmpty(drawer);
   settleEmptyDrawer(drawer, cart);
   return;
  }

  // [nexbie lazy-cart-drawer] 删除商品后推荐区已被隐藏（data-recs-key 已清），服务端购物车
  // 现在是最新的，按剩余商品重新加载推荐；商品构成未变时 loadCartRecommendations 会直接返回。
  // 赠品骨架正在显示时跳过：赠品引擎马上会再改一次购物车并整块重建抽屉，这一轮推荐请求
  // 只会和赠品写入抢带宽然后立刻作废，交给重建后的那一次。
  if (drawer.hasAttribute('open') && window.NexbieFreeGift?.hasPreview?.() !== true) {
   loadCartRecommendations(drawer, { forceImmediate: true });
  }

  // The optimistic drawer flow deliberately never emits cart:change while the
  // drawer is open, so assets/cart-free-gift.js would never learn that the
  // shopper just crossed a gift threshold from inside the drawer. This is that
  // signal: the server cart, after the whole queue has drained.
  document.documentElement.dispatchEvent(
   new CustomEvent('cart:reconciled', { bubbles: true, detail: { cart } })
  );
 };

 const showDrawerQtyError = (drawer, message) => {
  drawer.querySelector('.banner--error')?.remove();

  const banner = document.createElement('div');
  banner.className = 'banner banner--error';
  banner.setAttribute('role', 'alert');
  banner.textContent = message;
  drawer.prepend(banner);
  setTimeout(() => banner.remove(), 4500);
 };

 // knownCart 让调用方复用刚刚拉到的权威购物车，省掉一次重复的 cart.js 往返。
 const reconcileDrawer = async (drawer, message, knownCart = null) => {
  try {
   const cart =
    knownCart || (await nativeFetch(`${window.Shopify.routes.root}cart.js`).then((response) => response.json()));
   applyServerCart(drawer, cart);
   if (groupCartItems(cart.items).some((item) => !lineRowFor(drawer, item.key))) {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
   }
  } catch (error) {
   console.error('Cart drawer reconcile failed:', error);
  }

  if (message) {
   showDrawerQtyError(drawer, message);
  }
 };

 // /cart/change.js is not safe to call concurrently: each response is built
 // from the cart as it was when that request started, so two in-flight changes
 // for different lines overwrite each other and one of them is silently lost.
 // Deleting several lines in a row is enough to trigger it. Everything is
 // therefore funnelled through one queue with at most one request in flight,
 // and each entry reads its target at the moment it is sent so repeated clicks
 // on the same line collapse into the latest value.
 const drawerPendingTargets = new Map();
 let drawerFlushChain = Promise.resolve();

 const readServerCart = async () => {
  const response = await nativeFetch(`${window.Shopify.routes.root}cart.js`);
  if (!response.ok) throw new Error('Could not read cart');
  return response.json();
 };

 const postSingleDrawerChange = async (lineKey, quantity) => {
  const response = await nativeFetch(`${window.Shopify.routes.root}cart/change.js`, {
   method: 'POST',
   headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify({ id: lineKey, quantity })
  });
  const payload = await response.json();
  if (response.ok) return payload;
  const cart = await readServerCart();
  throw Object.assign(new Error('cart/change.js rejected the update'), {
   drawerMessage: payload?.description || payload?.message || 'That quantity is not available.',
   staleKey: !cart.items.some((item) => item.key === lineKey),
   cart
  });
 };

 const postDrawerChange = async (drawer, lineKey, quantity) => {
  const identity = drawerLineState.get(lineKey)?.identity || (
   lineRowFor(drawer, lineKey)?.hasAttribute('data-cart-group') ? rowGroupIdentity(lineRowFor(drawer, lineKey)) : null
  );
  if (!identity) return postSingleDrawerChange(lineKey, quantity);
  const change = (cart = null) => changeCartGroupQuantity({
   identity, quantity, cart, readCart: readServerCart, changeLine: postSingleDrawerChange
  });
  try {
   return await change();
  } catch (error) {
   // A concurrent discount or gift write may re-key the members. Retry the group once.
   if (error.staleKey) return change(error.cart);
   throw error;
  }
 };

 const flushDrawerQueue = (drawer) => {
  drawerFlushChain = drawerFlushChain.then(async () => {
   while (drawerPendingTargets.size > 0) {
    const [queuedKey] = drawerPendingTargets.keys();
    const quantity = drawerPendingTargets.get(queuedKey);
    drawerPendingTargets.delete(queuedKey);

    let cart = null;

    try {
     cart = await postDrawerChange(drawer, queuedKey, quantity);
    } catch (error) {
     drawerPendingTargets.clear();
     await reconcileDrawer(
      drawer,
      error?.drawerMessage || 'Could not update your cart. Please try again.',
      error?.cart || null
     );
     return;
    }

    // Only reconcile once the queue has drained, otherwise an intermediate
    // cart would repaint rows the user has already changed again.
    if (drawerPendingTargets.size === 0) {
     applyServerCart(drawer, cart);
    } else {
     // 不重绘，但 Key 必须原子跟上服务端：这一次响应可能已经跨过折扣阶梯，
     // 队列里排队待发的旧 Key 此刻就作废了，reconcileLineKeys 会把 DOM、
     // drawerLineState、drawerQtyTimers 与 drawerPendingTargets 一并改名。
     latestServerCart = cart;
     reconcileLineKeys(drawer, cart.items);
    }
   }
  });

  return drawerFlushChain;
 };

 /* ------------------------------------------------------------------------
    Silent cart writes.

    Shopify's web pixels manager replaces window.fetch and XMLHttpRequest from
    content_for_header, before any theme script runs, and turns every /cart/add
    and /cart/change into one product_added_to_cart / product_removed_from_cart
    per line. That is right for a shopper's own action and wrong for writes the
    theme makes on its behalf: the FBT bulk add would report N add-to-carts for
    one click, and the free gift engine would report a gift the shopper never
    chose, at its full price.

    nativeFetch above is NOT a way out — it is captured after the manager has
    already swapped window.fetch, so it is the instrumented one. A same-origin
    about:blank iframe, however, gets its own untouched globals while sharing
    the store's cookies, so its fetch reaches the same cart without being seen.
    The frame is created on first use and kept: creating one per write would
    cost a document each time and the pixel behaviour is identical.

    Callers that cannot reach it (script not loaded) simply fall back to
    window.fetch and are reported again — the cart write must never depend on
    this working.
    ------------------------------------------------------------------------ */
 let silentFrame = null;

 const silentWindow = () => {
  if (silentFrame?.isConnected && silentFrame.contentWindow) {
   return silentFrame.contentWindow;
  }

  const frame = document.createElement('iframe');

  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.setAttribute('title', '');
  frame.dataset.nexbieSilentCart = '';
  frame.src = 'about:blank';
  frame.style.display = 'none';
  document.body.appendChild(frame);
  silentFrame = frame;

  return frame.contentWindow;
 };

 /**
  * POST (or GET) a Cart AJAX endpoint without the pixel manager seeing it.
  *
  * @param {string} path - relative to Shopify.routes.root, e.g. 'cart/add.js'
  * @param {RequestInit} [init] - passed through untouched apart from `signal`
  * @returns {Promise<Response>} the iframe's Response; rejects with AbortError
  *   once CART_ADD_TIMEOUT_MS has passed, matching the wrapper above so a hung
  *   request cannot leave a caller's button parked in its busy state.
  */
 const silentFetch = (path, init = {}) => {
  const frameWindow = silentWindow();

  if (!frameWindow?.fetch) {
   return window.fetch(`${window.Shopify.routes.root}${path}`, init);
  }

  // A signal is a platform object, so the frame's fetch accepts this realm's
  // just as the wrapper above does — same merge, same fallback.
  const controller = new AbortController();
  const signal = init.signal
   ? (typeof AbortSignal.any === 'function' ? AbortSignal.any([init.signal, controller.signal]) : init.signal)
   : controller.signal;
  const request = frameWindow.fetch(`${window.Shopify.routes.root}${path}`, { ...init, signal });
  const timeout = setTimeout(() => controller.abort(), CART_ADD_TIMEOUT_MS);

  request.then(
   () => clearTimeout(timeout),
   () => clearTimeout(timeout)
  );

  return request;
 };

 // Same hazard, other writers: assets/cart-free-gift.js adds and removes gift
 // lines in reaction to the very cart changes this queue produces. Sharing one
 // lane is what keeps a gift write from overlapping a shopper's quantity change.
 // The wrapper can never reject, or a single failed task would poison the chain
 // and silently kill every later quantity update.
 window.NexbieCart = {
  enqueueMutation: (task) => {
   const run = () =>
    Promise.resolve()
     .then(task)
     .catch((error) => console.error('Cart mutation failed:', error));

   drawerFlushChain = drawerFlushChain.then(run, run);

   return drawerFlushChain;
  },
  hasPendingUserChange: () => drawerPendingTargets.size > 0 || drawerQtyTimers.size > 0,
  silentFetch
 };

 const queueQuantity = (drawer, lineKey, quantity) => {
  drawerPendingTargets.set(lineKey, quantity);
  clearTimeout(drawerQtyTimers.get(lineKey));
  const timer = setTimeout(() => {
   // Key migration may have renamed the timer while a previous write was in flight.
   drawerQtyTimers.forEach((value, key) => { if (value === timer) drawerQtyTimers.delete(key); });
   flushDrawerQueue(drawer);
  }, DRAWER_QTY_DEBOUNCE_MS);
  drawerQtyTimers.set(lineKey, timer);
 };

 const removedCartProductIds = new Set();

 const optimisticItemCount = () => [...drawerLineState.values()].reduce((sum, entry) => sum + entry.quantity, 0);

 // The empty state exactly as cart-drawer.liquid renders it: no free shipping bar, no list,
 // no footer, one centred line of copy (the string rides on the drawer as data-empty-text,
 // because the section only renders it when the cart is already empty). Idempotent.
 const paintEmpty = (drawer) => {
  if (drawer.querySelector('.nx-cart-empty')) {
   return;
  }

  const text = drawer.getAttribute('data-empty-text') || 'Your cart is empty';

  [...drawer.children].forEach((child) => {
   if (child.getAttribute('slot') !== 'header') {
    child.remove();
   }
  });
  drawer.insertAdjacentHTML('beforeend', `<p class="h5 text-center nx-cart-empty">${text}</p>`);
  drawer.classList.add('drawer--center-body');
 };

 // The drawer's own section id, asked for the way theme.js's ProductForm asks: Prestige
 // moves <cart-drawer> out to <body> once upgraded, so it no longer sits in its
 // .shopify-section wrapper and getSectionId() cannot read it off the DOM.
 const drawerSectionId = () => {
  const detail = { sections: [] };
  document.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail }));

  return detail.sections.find((id) => /cart-drawer/.test(id)) || detail.sections[0] || null;
 };

 // The server has confirmed an empty cart. The drawer already shows the empty copy
 // (paintEmpty), so the real empty section is fetched and swapped in silently — same
 // markup, no fade — and cart:change carries that HTML so theme.js's replaceContent takes
 // its no-rebuild path (it compares against _prefilledSectionHtml) while <cart-count>, the
 // free shipping bar and the gift script still get the cart. Anything unexpected falls
 // back to the theme's own cart:refresh rebuild.
 const settleEmptyDrawer = async (drawer, cart) => {
  resetDrawerState();

  const sectionId = drawerSectionId();
  const fallback = () => document.dispatchEvent(new CustomEvent('cart:refresh'));

  if (!sectionId) {
   fallback();
   return;
  }

  try {
   const root = window.Shopify.routes.root;
   const html = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`).then((response) => response.text());
   const source = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');

   if (!source) {
    fallback();
    return;
   }

   drawer.replaceChildren(...document.createRange().createContextualFragment(source.innerHTML).children);
   drawer.classList.add('drawer--center-body');
   drawer._prefilledSectionHtml = html;
   cart.sections = { [sectionId]: html };

   document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } })
   );
  } catch (error) {
   console.warn('Empty cart drawer settle failed, refreshing:', error);
   fallback();
  }
 };

 const changeDrawerQuantity = (drawer, lineKey, quantity) => {
  const target = Math.max(0, quantity);
  const input = lineRowFor(drawer, lineKey)?.querySelector('input[data-line-key]');
  if (!Number.isInteger(target) || input?.hasAttribute('max') && target > Number(input.max)) return;
  if (target === 0) {
   const row = lineRowFor(drawer, lineKey);
   const productId = row?.getAttribute('data-product-id') || row?.dataset?.productId;
   if (productId) {
    removedCartProductIds.add(String(productId));
   }
  }
  const state = stateFor(drawer, lineKey);
  const previous = state.quantity;

  if (target === previous) {
   return;
  }

  const unit = state.unitCents ?? 0;

  state.quantity = target;
  drawerStateDirty = true;

  paintLine(drawer, lineKey, target);
  paintTotals(
   drawer,
   Math.max(0, currentSubtotal(drawer) + (target - previous) * unit),
   [...drawerLineState.values()].reduce((sum, entry) => sum + entry.quantity, 0)
  );
  queueQuantity(drawer, lineKey, target);

  // Lets assets/cart-free-gift.js paint its skeleton gift row in this frame,
  // instead of after the two round trips the real gift line needs.
  document.documentElement.dispatchEvent(
   new CustomEvent('cart:optimistic-change', {
    bubbles: true,
    detail: { subtotalCents: optimisticGiftSubtotal() }
   })
  );

  if (target === 0) {
   // [nexbie lazy-cart-drawer] 商品行一删，推荐区同帧隐藏，不等 /cart/change 与抽屉重建。
   // 不在这里重新请求推荐：此刻服务端购物车还没改，section 会按删前的内容渲染。
   // 购物车仍有商品时，由 applyServerCart 在服务端同步完成后重新加载；
   // 删的是最后一件时保持隐藏，随后的空购物车重建也不会再显示它。
   hideCartRecommendations(drawer);
  }

  // 最后一件被删掉：同帧画出空购物车，而不是留一个只剩包邮条和页脚的空壳等服务端
  // （300ms 防抖 + /cart/change ~0.7s + section 拉取 ~0.4s + 淡入淡出，实测约 1.8s）。
  // 服务端确认后由 settleEmptyDrawer 用真实的空购物车 section 静默替换，画面不变。
  if (optimisticItemCount() === 0) {
   paintEmpty(drawer);
  }
 };

 // Both mobile and desktop selectors on /cart address the same complete group.
 const changePageGroup = async (row, quantity) => {
  if (row.hasAttribute('aria-busy')) return;
  row.setAttribute('aria-busy', 'true');
  row.querySelectorAll('input').forEach((input) => { input.disabled = true; });
  try {
   await window.NexbieCart.enqueueMutation(async () => {
    try {
     const change = (cart = null) => changeCartGroupQuantity({
      identity: rowGroupIdentity(row), quantity, cart, readCart: readServerCart, changeLine: postSingleDrawerChange
     });
     try { await change(); } catch (error) {
      if (!error.staleKey) throw error;
      await change(error.cart);
     }
     window.location.reload();
    } catch (error) {
     row.removeAttribute('aria-busy');
     row.querySelectorAll('input').forEach((input) => {
      input.disabled = false;
      input.value = input.defaultValue;
     });
     const alert = document.createElement('p');
     alert.setAttribute('role', 'alert');
     alert.textContent = error.drawerMessage || 'Could not update your cart. Please try again.';
     row.querySelector('[role="alert"]')?.remove();
     row.querySelector('td')?.append(alert);
    }
   });
  } finally {
   row.removeAttribute('aria-busy');
  }
 };
 document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest(CHANGE_LINK_SELECTOR) : null;
  const row = link?.closest('.cart-page [data-cart-group]');
  if (!row || !link.closest('line-item-quantity')) return;
  event.preventDefault();
  event.stopPropagation();
  const quantity = Number(new URL(link.href).searchParams.get('quantity'));
  if (Number.isInteger(quantity) && quantity >= 0) changePageGroup(row, quantity);
 }, true);
 document.addEventListener('change', (event) => {
  const input = event.target instanceof Element ? event.target.closest('input[data-line-key]') : null;
  const row = input?.closest('.cart-page [data-cart-group]');
  if (!row) return;
  event.stopPropagation();
  const quantity = Number(input.value);
  if (input.value !== '' && Number.isInteger(quantity) && quantity >= 0 && input.checkValidity()) {
   changePageGroup(row, quantity);
  } else {
   input.value = input.defaultValue;
  }
 }, true);

 const drawerFor = (target) => {
  if (window.themeVariables?.settings?.pageType === 'cart') {
   return null;
  }

  return target instanceof Element ? target.closest('cart-drawer') : null;
 };

 document.addEventListener(
  'click',
  (event) => {
   const link = event.target instanceof Element ? event.target.closest(CHANGE_LINK_SELECTOR) : null;

   if (!link || !link.closest('line-item-quantity')) {
    return;
   }

   const drawer = drawerFor(link);

   if (!drawer) {
    return;
   }

   // Beat theme.js's LineItemQuantity, whose Delegate is bound to the element
   // itself and therefore runs in the bubble phase.
   event.preventDefault();
   event.stopPropagation();

   const row = link.closest('line-item');
   const input = row?.querySelector('input[data-line-key]');
   const lineKey = input?.getAttribute('data-line-key');

   if (!lineKey) {
    return;
   }

   if (link.classList.contains(DELETE_LINK_CLASS)) {
    changeDrawerQuantity(drawer, lineKey, 0);
    return;
   }

   // Read the step from the live input rather than the href: the href holds
   // the quantity that was correct when the section was rendered, which is
   // exactly what breaks fast repeated clicks.
   const step = Math.max(1, parseInt(input.step, 10) || 1);
   const selector = link.closest('quantity-selector');
   const anchor = selector?.querySelector('quantity-input');
   const isDecrease = anchor
    ? Boolean(link.compareDocumentPosition(anchor) & Node.DOCUMENT_POSITION_FOLLOWING)
    : false;
   const state = stateFor(drawer, lineKey);

   changeDrawerQuantity(drawer, lineKey, state.quantity + (isDecrease ? -step : step));
  },
  true
 );

 document.addEventListener(
  'change',
  (event) => {
   const input = event.target instanceof Element ? event.target.closest('input[data-line-key]') : null;

   if (!input) {
    return;
   }

   const drawer = drawerFor(input);

   if (!drawer) {
    return;
   }

   event.stopPropagation();

   const lineKey = input.getAttribute('data-line-key');
   const typed = parseInt(input.value, 10);

   if (!lineKey || !Number.isInteger(typed) || typed < 0 || typed !== 0 && !input.checkValidity()) {
    if (lineKey) paintLine(drawer, lineKey, stateFor(drawer, lineKey).quantity);
    return;
   }

   changeDrawerQuantity(drawer, lineKey, typed);
  },
  true
 );

 // Everything above deliberately avoids cart:change so the drawer is never
 // rebuilt under the user. Once it closes there is nothing left to protect, so
 // publish the real cart for <cart-count>, fetchCart and any third-party
 // listener. baseEvent is not "variant:add", so CartDrawer will not reopen.
 document.addEventListener('dialog:after-hide', (event) => {
  if (!(event.target instanceof Element) || event.target.tagName !== 'CART-DRAWER') {
   return;
  }

  if (!drawerStateDirty || !latestServerCart) {
   return;
  }

  drawerStateDirty = false;
  document.documentElement.dispatchEvent(
   new CustomEvent('cart:change', {
    bubbles: true,
    detail: { baseEvent: 'line-item:change', cart: latestServerCart }
   })
  );
 }, true);
})();

/*
 * Judge.me's 2025 review widget can occasionally omit review-media thumbnails
 * during its initial hydration. Temporarily changing the native sort makes
 * the widget mount the same media correctly. Until Judge.me fixes that bug,
 * do the remount once, silently, and only when the widget reports media but
 * none is attached to the reviews currently displayed.
 */
(() => {
 if (!window.location.pathname.startsWith('/products/')) {
  return;
 }

 const widgetSelector = '#judgeme_product_reviews';

 const pause = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

 const changeSort = (select, value) => {
  select.value = value;
  select.dispatchEvent(new Event('input', { bubbles: true }));
  select.dispatchEvent(new Event('change', { bubbles: true }));
 };

 const refreshMissingReviewMedia = async () => {
  const widget = document.querySelector(widgetSelector);

  if (!widget || widget.dataset.nexbieMediaRefreshAttempted === 'true') {
   return;
  }

  const currentReviewHasMedia = () => widget.querySelectorAll(
   '[review-uuid] img[alt="Review Image"]'
  ).length > 0;
  const hasKnownMedia = () => widget.querySelectorAll('.jm-media-grid img').length > 0;

  if (currentReviewHasMedia() || !hasKnownMedia()) {
   return;
  }

  widget.dataset.nexbieMediaRefreshAttempted = 'true';
  const sortSelect = widget.querySelector('.jdgm-sort-dropdown');
  if (!sortSelect || sortSelect.value === 'pictures_first') {
   return;
  }

  const originalSort = sortSelect.value;
  changeSort(sortSelect, 'pictures_first');
  await pause(600);
  changeSort(sortSelect, originalSort);
 };

 const observeWidget = () => {
  const observer = new MutationObserver(() => {
   if (document.querySelector(widgetSelector)) {
    observer.disconnect();
    window.setTimeout(refreshMissingReviewMedia, 1200);
   }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.setTimeout(() => observer.disconnect(), 15000);
 };

 if (document.querySelector(widgetSelector)) {
  window.setTimeout(refreshMissingReviewMedia, 1200);
 } else {
  observeWidget();
 }
})();

/**
 * [nexbie pager-align]
 * 轮播翻页按钮要对齐到商品卡片「图片区」的竖直中点，而不是整张卡片的中点
 * （卡片图片下面还有标题和价格，两个中点差几十像素）。
 *
 * 图片区高度只能从实际布局里量：它由图片自身比例决定，CSS 的纵向属性拿不到
 * 兄弟元素的高度，而按断点把主题的「每行几张」上限再抄一份，主题一改就会
 * 悄悄漂移。所以这里量出真实高度写进 --nx-pager-media-height，由 CSS 取用。
 *
 * 容器上带 data-nx-pager-align="<图片区选择器>"。量到之后才加
 * nx-pager-aligned 类，CSS 用这个类做覆盖，JS 没跑时仍回退到卡片中点。
 */
(() => {
 const CONTAINER_SELECTOR = '[data-nx-pager-align]';

 const syncContainer = (container) => {
  const media = container.querySelector(container.dataset.nxPagerAlign);

  if (!media) return;

  const height = media.getBoundingClientRect().height;

  if (!height) return;

  container.style.setProperty('--nx-pager-media-height', height + 'px');
  container.classList.add('nx-pager-aligned');
 };

 const syncAll = () => document.querySelectorAll(CONTAINER_SELECTOR).forEach(syncContainer);

 let ticking = false;
 const requestSync = () => {
  if (ticking) return;
  ticking = true;
  window.requestAnimationFrame(() => {
   ticking = false;
   syncAll();
  });
 };

 // Related products 整个 section 是 <product-recommendations> 异步取回后注入的，
 // 注入、图片加载、视口变化都会改变这个壳的尺寸，一个 ResizeObserver 全覆盖。
 const observeHosts = () => {
  if (typeof ResizeObserver !== 'function') return;

  const resizeObserver = new ResizeObserver(requestSync);

  document.querySelectorAll('product-recommendations').forEach((host) => resizeObserver.observe(host));
  document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => resizeObserver.observe(container));
 };

 const start = () => {
  syncAll();
  observeHosts();
  window.addEventListener('resize', requestSync, { passive: true });
 };

 if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
 } else {
  start();
 }
})();

/*
 * [nexbie skeleton-helpers]
 * 下面两处加载骨架共用：造一根骨架条。底色 / 动画统一用赠品行骨架的
 * .cart-free-gift-skeleton（custom.css），含它既有的 prefers-reduced-motion 规则。
 */
const nxSkeletonBar = (...classNames) => {
 const bar = document.createElement('span');
 bar.className = ['cart-free-gift-skeleton', ...classNames].join(' ');
 return bar;
};

/*
 * [nexbie jdgm-hydration-skeleton]
 * Judge.me 评论组件先服务端直出评论（.jdgm-ssr-reviews），脚本接管时换成 .jm-review-widget，
 * 其主体 .jm-review-widget__body 在取到数据前没有元素子节点：整块从直出时的约 1200px 塌到 96px，
 * 约 1s 后才长回（实测本地预览桌面 1201 → 96 → 1497，移动 1191 → 96 → 1961）。
 *
 * 接管那一刻在组件外层容器（.shopify-app-block，主题侧 DOM）锁住直出时的高度，
 * 空出来的区域铺评论骨架；主体出现元素子节点后移除骨架，再等组件长回直出高度（或 1.5s）
 * 后解锁。15s 兜底全部撤掉，Judge.me 加载失败时不会一直占位。
 * 不改 Judge.me 内部节点，只读它的类名判断阶段。
 */
(() => {
 if (!window.location.pathname.startsWith('/products/')) return;
 if (typeof ResizeObserver !== 'function' || typeof MutationObserver !== 'function') return;

 const ROW_COUNT = 8;

 const buildSkeleton = () => {
  const skeleton = document.createElement('div');
  skeleton.className = 'nx-jdgm-skel';
  skeleton.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < ROW_COUNT; i += 1) {
   const row = document.createElement('div');
   row.className = 'nx-jdgm-skel__row';
   const lines = document.createElement('div');
   lines.className = 'nx-jdgm-skel__lines';
   lines.append(nxSkeletonBar('nx-jdgm-skel__line'), nxSkeletonBar('nx-jdgm-skel__line'), nxSkeletonBar('nx-jdgm-skel__line'));
   row.append(nxSkeletonBar('nx-jdgm-skel__stars'), nxSkeletonBar('nx-jdgm-skel__name'), lines);
   skeleton.append(row);
  }
  return skeleton;
 };

 const watch = (widget) => {
  const host = widget.closest('.shopify-app-block') || widget.parentElement;
  if (!host || widget.dataset.nxSkelWatched === 'true') return;
  widget.dataset.nxSkelWatched = 'true';

  let ssrHeight = 0;
  let skeleton = null;
  let locked = false;
  let done = false;
  let releaseTimer = 0;

  const bodyOf = () => widget.querySelector('.jm-review-widget__body');

  const release = () => {
   if (done) return;
   done = true;
   window.clearTimeout(releaseTimer);
   skeleton?.remove();
   skeleton = null;
   host.classList.remove('nx-jdgm-skel-host');
   host.style.removeProperty('min-height');
   resizeObserver.disconnect();
   mutationObserver.disconnect();
  };

  const place = () => {
   if (!skeleton) return;
   skeleton.style.top = `${widget.offsetTop + widget.offsetHeight}px`;
  };

  const sync = () => {
   if (done) return;
   const height = widget.getBoundingClientRect().height;
   if (!locked) {
    if (widget.querySelector('.jdgm-ssr-reviews')) {
     ssrHeight = height;
     return;
    }
    const body = bodyOf();
    if (!body || body.childElementCount > 0 || ssrHeight < 200) {
     // 接管时主体已有内容、或没有可锁的直出高度：不需要骨架
     if (body) release();
     return;
    }
    locked = true;
    host.classList.add('nx-jdgm-skel-host');
    host.style.minHeight = `${ssrHeight}px`;
    skeleton = buildSkeleton();
    host.append(skeleton);
    place();
    window.setTimeout(release, 15000);
    return;
   }
   const body = bodyOf();
   if (skeleton && body && body.childElementCount > 0) {
    skeleton.remove();
    skeleton = null;
    releaseTimer = window.setTimeout(release, 1500);
   }
   place();
   if (!skeleton && height >= ssrHeight) release();
  };

  const resizeObserver = new ResizeObserver(sync);
  const mutationObserver = new MutationObserver(sync);
  resizeObserver.observe(widget);
  mutationObserver.observe(widget, { childList: true, subtree: true });
  // 组件一直停在直出态（未接管）时也不无限期盯着
  window.setTimeout(release, 20000);
  sync();
 };

 const start = () => {
  const widget = document.querySelector('.jdgm-review-widget');
  if (widget) watch(widget);
 };

 if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
 } else {
  start();
 }
})();

/*
 * [nexbie facet-skeleton]
 * 集合页筛选：theme.js 收到 facet:update 后取整个 section，取回前保留旧商品列表、页顶一条加载条。
 * 超过 300ms 仍未返回时，给现有每张商品卡盖一层同尺寸骨架（图位 + 标题 + 价格），
 * 原卡不移除、列表高度不变，页面不会因筛选跳动。缓存命中（cachedFetch）通常在阈值内返回，不闪。
 * 取回后 theme.js 整块替换 section 子节点，骨架随旧卡一起消失；theme:loading:end 在替换前同步派发，
 * 也顺手清一次。请求失败时 theme.js 静默吞掉、不发结束事件，所以 12s 兜底恢复原列表。
 */
(() => {
 const DELAY = 300;
 const FAILSAFE = 12000;
 let showTimer = 0;
 let failsafeTimer = 0;
 let activeList = null;

 const clear = () => {
  window.clearTimeout(showTimer);
  window.clearTimeout(failsafeTimer);
  if (!activeList) return;
  activeList.classList.remove('nx-facet-loading');
  activeList.querySelectorAll('.nx-facet-skel').forEach((node) => node.remove());
  activeList.removeAttribute('aria-busy');
  activeList = null;
 };

 const cover = (list) => {
  list.querySelectorAll('.product-card').forEach((card) => {
   const figure = card.querySelector('.product-card__figure');
   const overlay = document.createElement('span');
   overlay.className = 'nx-facet-skel';
   overlay.setAttribute('aria-hidden', 'true');
   const media = nxSkeletonBar('nx-facet-skel__media');
   if (figure) media.style.blockSize = `${figure.offsetHeight}px`;
   overlay.append(media, nxSkeletonBar('nx-facet-skel__title'), nxSkeletonBar('nx-facet-skel__title', 'nx-facet-skel__title--short'), nxSkeletonBar('nx-facet-skel__price'));
   card.append(overlay);
  });
  list.classList.add('nx-facet-loading');
  list.setAttribute('aria-busy', 'true');
  activeList = list;
 };

 document.addEventListener('facet:update', (event) => {
  clear();
  const sectionId = event.detail?.url?.searchParams?.get('section_id');
  const section = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
  const list = section?.querySelector('product-list, .product-list');
  if (!list) return;
  showTimer = window.setTimeout(() => {
   if (!list.isConnected) return;
   cover(list);
   failsafeTimer = window.setTimeout(clear, FAILSAFE);
  }, DELAY);
 });

 document.addEventListener('theme:loading:end', clear);
})();

/*
 * [nexbie predictive-search-skeleton]
 * 移动端页眉搜索（桌面端点搜索会跳 /search 整页，结果服务端直出，没有这个面板）：
 * theme.js 在输入停 280ms 后才去取 /search/suggest，取回前结果区完全是空的，
 * 只有页顶一条加载条。首次输入（结果区为空）时先放一份骨架，结构复用真实结果的类名
 * （建议词块 + 商品卡行），间距由主题同一批规则给出。
 * 已有结果时不换：theme.js 本来就保留旧结果直到新结果替换。取回后 theme.js 的
 * replaceChildren 自然换掉骨架；清空输入时它把结果区置空。请求失败 theme.js 不会收尾，
 * 所以 12s 后若结果区仍只有骨架，就清掉，回到原来的空态。
 */
(() => {
 const SKELETON_CLASS = 'nx-ps-skel';
 const PRODUCT_ROWS = 4;

 const buildSkeleton = () => {
  const results = document.createElement('div');
  results.className = `predictive-search__results ${SKELETON_CLASS}`;
  results.setAttribute('aria-hidden', 'true');

  const suggestions = document.createElement('div');
  suggestions.className = 'predictive-search__resource-item';
  const suggestionStack = document.createElement('div');
  suggestionStack.className = 'v-stack gap-6 sm:gap-8';
  const chips = document.createElement('div');
  chips.className = 'nx-ps-skel__chips';
  chips.append(nxSkeletonBar('nx-ps-skel__chip'), nxSkeletonBar('nx-ps-skel__chip'), nxSkeletonBar('nx-ps-skel__chip'));
  suggestionStack.append(nxSkeletonBar('nx-ps-skel__category'), chips);
  suggestions.append(suggestionStack);

  // 与真实结构一致：商品行在标签面板里，标签栏与商品之间不吃结果区的行间距
  const tabs = document.createElement('div');
  tabs.className = 'nx-ps-skel__tabs';
  const tabBar = document.createElement('div');
  tabBar.className = 'nx-ps-skel__tabbar';
  tabBar.append(nxSkeletonBar('nx-ps-skel__tab'), nxSkeletonBar('nx-ps-skel__tab'));
  tabs.append(tabBar);

  const products = document.createElement('div');
  products.className = 'predictive-search__products nx-ps-skel__products';
  for (let i = 0; i < PRODUCT_ROWS; i += 1) {
   const card = document.createElement('div');
   card.className = 'horizontal-product-card';
   const figure = document.createElement('span');
   figure.className = 'horizontal-product-card__figure';
   figure.append(nxSkeletonBar('nx-ps-skel__media'));
   const info = document.createElement('div');
   info.className = 'horizontal-product-card__info';
   const text = document.createElement('div');
   text.className = 'nx-ps-skel__text';
   text.append(nxSkeletonBar('nx-ps-skel__title'), nxSkeletonBar('nx-ps-skel__title', 'nx-ps-skel__title--short'), nxSkeletonBar('nx-ps-skel__price'));
   info.append(text);
   card.append(figure, info);
   products.append(card);
  }

  tabs.append(products);
  results.append(suggestions, tabs);
  return results;
 };

 document.addEventListener('input', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'q' || input.value.trim() === '') return;
  const ownerId = input.form?.getAttribute('aria-owns');
  const predictive = ownerId ? document.getElementById(ownerId) : null;
  if (!predictive || predictive.tagName !== 'PREDICTIVE-SEARCH') return;
  const slot = predictive.querySelector('[slot="results"]');
  if (!slot || slot.childElementCount > 0) return;

  const skeleton = buildSkeleton();
  slot.replaceChildren(skeleton);
  window.setTimeout(() => {
   if (slot.firstElementChild === skeleton) slot.replaceChildren();
  }, 12000);
 });
})();
